import React, { useState, useEffect } from 'react';
import { getUser } from '../services/auth';
import { Link } from 'react-router-dom';
import { analyticsAPI } from '../services/api';
import StatCard from '../components/dashboard/StatCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler,
);

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adminName, setAdminName] = useState('');

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    useEffect(() => {
        const user = getUser();
        if (user) {
            setAdminName(user.name || 'Admin');
        } else {
            setAdminName('Admin');
        }
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            const response = await analyticsAPI.getDashboard();
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch dashboard stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <LoadingSpinner message="Loading dashboard..." />;
    }

    // Chart configurations
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
            },
        },
    };

    // User Growth Chart Data
    const userGrowthData = {
        labels: stats?.user_growth_chart?.labels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [
            {
                label: 'New Users',
                data: stats?.user_growth_chart?.data || [0, 0, 0, 0, 0, 0, 0],
                borderColor: 'rgb(99, 102, 241)',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    // Engagement Chart Data
    const engagementData = {
        labels: ['Daily Logs', 'Prayer Completions', 'Habit Completions', 'Notes', 'Qada'],
        datasets: [
            {
                label: 'This Week',
                data: [
                    stats?.engagement_chart?.daily_logs_count || 0,
                    stats?.engagement_chart?.prayer_completions || 0,
                    stats?.engagement_chart?.habit_completions || 0,
                    stats?.engagement_chart?.notes_count || 0,
                    stats?.engagement_chart?.qada_completed || 0,
                ],
                backgroundColor: [
                    'rgba(99, 102, 241, 0.8)',
                    'rgba(34, 197, 94, 0.8)',
                    'rgba(251, 146, 60, 0.8)',
                    'rgba(168, 85, 247, 0.8)',
                    'rgba(236, 72, 153, 0.8)',
                ],
                borderColor: [
                    'rgb(99, 102, 241)',
                    'rgb(34, 197, 94)',
                    'rgb(251, 146, 60)',
                    'rgb(168, 85, 247)',
                    'rgb(236, 72, 153)',
                ],
                borderWidth: 2,
            },
        ],
    };

    // Content Distribution Chart
    const contentData = {
        labels: ['Articles', 'Duas', 'Insights'],
        datasets: [
            {
                data: [
                    stats?.content?.total_articles || 0,
                    stats?.content?.total_duas || 0,
                    stats?.content?.total_insights || 0,
                ],
                backgroundColor: [
                    'rgba(99, 102, 241, 0.8)',
                    'rgba(34, 197, 94, 0.8)',
                    'rgba(251, 146, 60, 0.8)',
                ],
                borderColor: [
                    'rgb(99, 102, 241)',
                    'rgb(34, 197, 94)',
                    'rgb(251, 146, 60)',
                ],
                borderWidth: 2,
            },
        ],
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
        },
    };


    return (
        <div>
            <div className="page-header mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="page-title">Welcome Back, {adminName}! 👋</h2>
                        <p className="page-subtitle">Here's what's happening with your app today</p>
                    </div>
                    <button className="btn btn-primary" onClick={fetchDashboardStats}>
                        <i className="bi bi-arrow-clockwise me-2"></i>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <StatCard
                        icon="bi-people-fill"
                        label="Total Users"
                        value={stats?.users?.total_users || 0}
                        trend={stats?.users?.new_this_month || 0}
                        trendLabel="new this month"
                        color="primary"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-file-text-fill"
                        label="Total Articles"
                        value={stats?.content?.total_articles || 0}
                        trend={stats?.content?.published_articles || 0}
                        trendLabel="published"
                        color="success"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-book-fill"
                        label="Total Duas"
                        value={stats?.content?.total_duas || 0}
                        trend={stats?.content?.published_duas || 0}
                        trendLabel="published"
                        color="info"
                    />
                </div>
                <div className="col-md-3">
                    <StatCard
                        icon="bi-heart-fill"
                        label="Active Habits"
                        value={stats?.engagement?.active_habits || 0}
                        trend={stats?.engagement?.habit_completion_rate || 0}
                        trendLabel="completion rate"
                        color="warning"
                    />
                </div>
            </div>

            {/* Charts Row 1 */}
            <div className="row g-4 mb-4">
                {/* User Growth Chart */}
                <div className="col-lg-8">
                    <div className="stat-card">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                                <i className="bi bi-graph-up-arrow me-2 text-primary"></i>
                                User Growth (Last 7 Days)
                            </h5>
                            <Link to="/analytics" className="btn btn-sm btn-outline-primary">
                                View Details
                            </Link>
                        </div>
                        <div style={{ height: '300px' }}>
                            <Line data={userGrowthData} options={chartOptions} />
                        </div>
                    </div>
                </div>

                {/* Content Distribution */}
                <div className="col-lg-4">
                    <div className="stat-card">
                        <h5 className="mb-3">
                            <i className="bi bi-pie-chart-fill me-2 text-success"></i>
                            Content Distribution
                        </h5>
                        <div style={{ height: '300px' }}>
                            <Doughnut data={contentData} options={doughnutOptions} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="row g-4 mb-4">
                {/* Engagement Overview */}
                <div className="col-lg-8">
                    <div className="stat-card">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                                <i className="bi bi-bar-chart-fill me-2 text-info"></i>
                                User Engagement Overview
                            </h5>
                            <Link to="/analytics" className="btn btn-sm btn-outline-primary">
                                View Details
                            </Link>
                        </div>
                        <div style={{ height: '300px' }}>
                            <Bar data={engagementData} options={chartOptions} />
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="col-lg-4">
                    <div className="stat-card">
                        <h5 className="mb-3">
                            <i className="bi bi-lightning-charge-fill me-2 text-warning"></i>
                            Quick Actions
                        </h5>
                        <div className="d-grid gap-2">
                            <Link to="/articles/new" className="btn btn-outline-primary">
                                <i className="bi bi-plus-circle me-2"></i>
                                Create Article
                            </Link>
                            <Link to="/duas/new" className="btn btn-outline-success">
                                <i className="bi bi-plus-circle me-2"></i>
                                Create Dua
                            </Link>
                            <Link to="/insights/new" className="btn btn-outline-info">
                                <i className="bi bi-plus-circle me-2"></i>
                                Create Insight
                            </Link>
                            <Link to="/notifications" className="btn btn-outline-warning">
                                <i className="bi bi-bell me-2"></i>
                                Send Notification
                            </Link>
                            <Link to="/media" className="btn btn-outline-secondary">
                                <i className="bi bi-image me-2"></i>
                                Upload Media
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            {stats?.recent_activity && stats.recent_activity.length > 0 && (
                <div className="stat-card">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                            <i className="bi bi-clock-history me-2 text-secondary"></i>
                            Recent Admin Activity
                        </h5>
                        <Link to="/activity-log" className="btn btn-sm btn-outline-secondary">
                            View All
                        </Link>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Admin</th>
                                    <th>Action</th>
                                    {/* <th>Details</th> */}
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.recent_activity.slice(0, 10).map((activity, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="avatar-circle me-2">
                                                    {activity.admin_name?.charAt(0).toUpperCase()}
                                                </div>
                                                <span>{activity.admin_name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge badge-primary text-black text-capitalize">
                                                {activity.action.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        {/* <td>
                                            <small className="text-muted">{activity.details}</small>
                                        </td> */}
                                        <td>
                                            <small className="text-muted">
                                                {new Date(activity.created_at).toLocaleString()}
                                            </small>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* System Status Footer */}
            <div className="row g-4 mt-2">
                <div className="col-md-4">
                    <div className="stat-card text-center">
                        <i className="bi bi-server fs-1 text-success d-block mb-2"></i>
                        <h6 className="mb-1">System Status</h6>
                        <span className="badge badge-success">All Systems Operational</span>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="stat-card text-center">
                        <i className="bi bi-database fs-1 text-info d-block mb-2"></i>
                        <h6 className="mb-1">Database</h6>
                        <span className="badge badge-info">Connected</span>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="stat-card text-center">
                        <i className="bi bi-shield-check fs-1 text-primary d-block mb-2"></i>
                        <h6 className="mb-1">Last Backup</h6>
                        <small className="text-muted">Today at 2:00 AM</small>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;